import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DEFAULTVALUE from '@/app/constants/defaultValue';
import { DetailsTrainingItem, TrainingMember } from '@/app/models/detailsTraining';
import { PersonResponse } from '@/app/models/person';
import { useUpdateDetailsTrainingMutation } from '@/app/services/detailsTrainingApi';
import { useSearchPersonQuery } from '@/app/services/personApi';
import { formatDateForBackend } from '@/app/utils/dateUtils';
import formatterMonetaryValue, {
  cpfOrCpnjFromBack,
  formatCurrency,
  formatHandleCPFAndCNPJ,
  isSupplierCodeValid,
} from '@/app/utils/numberUtils';
import FormFieldDate from '@/components/common/DateField';
import DrawerButtons from '@/components/common/DrawerButtons';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import TextButton from '@/components/common/TextButton';
import { usePeriod } from '@/context/PeriodContext';
import useFormattedCpfAndCpnj from '@/hooks/useFormattedCpfAndCpnj';
import useFormattedCurrency from '@/hooks/useFormattedCurrency';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import FormField from '../baseForms/FormField';
import FormFieldLong from '../baseForms/FormFieldLong';
import LaunchDetailsBox from '../baseForms/info/LaunchDetailsBox';
import PersonsTable from './PersonsTable';

interface NewEditTrainingDetailsProps {
  selectItem: DetailsTrainingItem;
  setDrawerOpen: (open: boolean) => void;
}

export default function NewEditTrainingDetails(props: NewEditTrainingDetailsProps) {
  const { t, i18n } = useTranslation(['training', 'common']);

  const theme = useTheme();
  const { selectItem, setDrawerOpen } = props;
  const [launchComplement] = useState<string>(selectItem?.complement ? selectItem.complement : '');

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const { value: totalValue, setValue: setTotalValue } = useFormattedCurrency(selectItem?.value);
  const { value: supplierCode, setValue: setSupplierCode } = useFormattedCpfAndCpnj(selectItem?.supplierCode);

  const [plannedInitDate, setPlannedInitDate] = useState<string>(
    selectItem?.initDate ? dayjs(selectItem.initDate).format('DD/MM/YYYY') : '',
  );
  const [plannedEndDate, setPlannedEndDate] = useState<string>(
    selectItem?.endDate ? dayjs(selectItem.endDate).format('DD/MM/YYYY') : '',
  );

  const [objecives, setObjecives] = useState<string | null>(selectItem?.objective ? selectItem?.objective : '');
  const [supplier, setSupplier] = useState<string | null>(selectItem?.supplier ? selectItem?.supplier : '');
  const [themeItem, setThemeItem] = useState<string | null>(selectItem?.theme ? selectItem?.theme : '');

  const [hourDuration, setHourDuration] = useState<string | null>(
    selectItem?.hourDuration != null ? String(selectItem.hourDuration) : '',
  );

  const [assignedCollaborators, setAssignedCollaborators] = useState<TrainingMember[]>([]);

  const [originPerson, setOriginPerson] = useState<PersonResponse | null>(null);
  const [originPersonSearch, setOriginPersonSearch] = useState('');
  const { data: originPersons, isFetching: isLoadingOrigin } = useSearchPersonQuery(
    {
      searchTerm: originPersonSearch,
      page: 0,
      size: 10,
      sortKey: '',
    },
    {
      skip: originPersonSearch.length < 1,
    },
  );

  useEffect(() => {
    if (selectItem.participants && selectItem.participants.length > 0) {
      const newCollaborators = selectItem.participants
        .filter((value) => value.id !== null)
        .map((item) => {
          const newCollaborator = {
            id: item.id,
            name: item.name,
            personId: item.personId,
          };
          return newCollaborator;
        });
      setAssignedCollaborators(newCollaborators);
    }
  }, [selectItem.participants]);

  const [justification, setJustification] = useState<string>(selectItem?.justify ? selectItem.justify : '');
  const [activity, setActivity] = useState<string | null>(selectItem?.activities ? selectItem?.activities : '');

  const [expenseDate, setExpenseDate] = useState<string>(
    selectItem?.expenseDate ? dayjs(selectItem?.expenseDate).format('DD/MM/YYYY') : '',
  );

  const { selectedPeriod } = usePeriod();

  const [update] = useUpdateDetailsTrainingMutation();

  const handleSubmit = async () => {
    setDrawerOpen(false);
    await toast.promise(
      update({
        id: selectItem.id,
        participants: assignedCollaborators.map((c) => ({ personId: c.personId })) || [],
        period: selectedPeriod?.date as string,
        objective: objecives as string,
        initDate: formatDateForBackend(plannedInitDate),
        endDate: formatDateForBackend(plannedEndDate),
        justify: justification as string,
        value: parseFloat(totalValue.replace('R$', '').replace('.', '').replace(',', '.')) || DEFAULTVALUE,
        hourDuration: hourDuration || '',
        supplier: supplier as string,
        supplierCode: cpfOrCpnjFromBack(supplierCode),
        theme: themeItem as string,
        activities: activity as string,
        expenseDate: formatDateForBackend(expenseDate as string),
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('updated.successfully') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const hasNegative = val.includes('-');
    const cleaned = val.replace(/[^0-9]/g, '');
    const raw = hasNegative ? `-${cleaned}` : cleaned;
    setTotalValue(formatCurrency(raw));
  };

  const handleAddCollaborator = () => {
    if (!originPerson) return;

    const newCollaborator: TrainingMember = {
      id: originPerson.id,
      name: originPerson.name,
      personId: originPerson.id,
    };

    setAssignedCollaborators((prevValue) => [...prevValue, newCollaborator]);
    setOriginPerson('' as unknown as PersonResponse);
  };

  const handleRemove = (removedCollaborator: number) => {
    setAssignedCollaborators((prevValue) => prevValue.filter((row) => row.id !== removedCollaborator));
  };

  const alreadyIncluded = (value: PersonResponse) => {
    const filteredProjects = assignedCollaborators.filter((item) => item.id === value.id);
    return filteredProjects.length >= 1;
  };

  const handleSupplierCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatHandleCPFAndCNPJ(e.target.value);
    setSupplierCode(formatted);
  };
  const supplierCodeIsValid = isSupplierCodeValid(supplierCode);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('common:edit.detail')} />

        <LaunchDetailsBox
          totalValue={formatterMonetaryValue(selectItem?.value || null)}
          launchComplement={launchComplement}
          complementDisabled
        />

        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mt: 2 }} />

        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: 1,
            gap: 2,
            flexWrap: 'wrap',
          }}
        />
        <Box sx={{ display: 'block', minWidth: theme.spacing(31), mb: 2 }}>
          <FormField label={t('common:total.value') as string} value={totalValue} onChange={handleValueChange} />
          <FormField
            label={t('common:supplier') as string}
            value={supplier as string}
            onChange={(e) => setSupplier(e.target.value)}
          />

          <FormField
            label={t('common:cnpj') as string}
            value={supplierCode as string}
            onChange={handleSupplierCode}
            error={!supplierCodeIsValid}
          />

          <FormField
            label={t('common:objective') as string}
            value={objecives as string}
            onChange={(e) => setObjecives(e.target.value)}
            rows={2}
            multiline
          />

          <FormField
            label={t('subject') as string}
            value={themeItem as string}
            onChange={(e) => setThemeItem(e.target.value)}
          />

          <Box sx={{ mt: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="fieldHeader" width={theme.spacing(17)}>
                  {t('common:period')}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <DatePicker
                      format="DD/MM/YYYY"
                      value={plannedInitDate ? dayjs(plannedInitDate, 'DD/MM/YYYY') : null}
                      onChange={(newValue: Dayjs | null) =>
                        setPlannedInitDate(newValue ? newValue.format('DD/MM/YYYY') : '')
                      }
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                          placeholder: t('common:start') as string,
                        },
                      }}
                      slots={{ openPickerIcon: () => null }}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <DatePicker
                      format="DD/MM/YYYY"
                      value={plannedEndDate ? dayjs(plannedEndDate, 'DD/MM/YYYY') : null}
                      onChange={(newValue: Dayjs | null) =>
                        setPlannedEndDate(newValue ? newValue.format('DD/MM/YYYY') : '')
                      }
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                          placeholder: t('common:end') as string,
                        },
                      }}
                      slots={{ openPickerIcon: () => null }}
                    />
                  </Box>
                </Stack>
              </Box>
            </LocalizationProvider>
          </Box>

          <FormField
            label={t('hour.load') as string}
            value={hourDuration as string}
            onChange={(e) => setHourDuration(e.target.value)}
          />
          <FormFieldDate label={t('common:expenseDate')} value={expenseDate as string} onChange={setExpenseDate} />

          <FormFieldLong
            label={t('activity')}
            value={activity as string}
            onChange={(e) => setActivity(e.target.value)}
            rows={2}
          />

          <FormFieldLong
            label={t('common:justification')}
            value={justification as string}
            onChange={(e) => setJustification(e.target.value)}
            rows={2}
          />

          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ width: '80%', display: 'flex', gap: 2, alignItems: 'center' }}
            >
              <Typography variant="fieldHeader" width={theme.spacing(24)}>
                {t('common:collaborator')}
              </Typography>
              <GenericAutoComplete
                value={originPerson}
                onChange={(value) => {
                  setOriginPerson(value);
                }}
                equalityTest={(option, value) => option?.personBadge === value?.personBadge}
                getOptionLabel={(option) => (option ? `${option?.personBadge ?? ''} - ${option.name ?? ''}` : '')}
                onSearch={(value) => {
                  setOriginPersonSearch(value);
                }}
                options={originPersonSearch !== '' && originPersons?.content ? originPersons.content : []}
                placeholder={t('common:type.to.search') as string}
                loading={isLoadingOrigin}
                sx={{ width: theme.spacing(80) }}
              />
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextButton
                sx={{ ml: 'auto' }}
                text={`+ ${t('add')}`}
                disabled={!originPerson || alreadyIncluded(originPerson)}
                onClick={handleAddCollaborator}
              />
            </Box>
          </Box>
          <Box sx={{ mt: 1 }}>
            <PersonsTable persons={assignedCollaborators as TrainingMember[]} onRemove={handleRemove} />
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} disabled={!plannedEndDate || !expenseDate} />
      </Box>
    </Box>
  );
}
